import { Schema } from 'prosemirror-model'
import { type EditorView } from 'prosemirror-view'
import { COMMON_TLDS } from 'utils/constants'
import { InternalUrl, isAlmanacUrl } from 'utils/urlUtils'
import { isMarkActive } from './isMarkActive'
import { addMarkInputRule } from './marks'

const EXTERNAL_LINK_REL = 'noopener noreferrer nofollow ugc' as const

interface InternalLinkAttributes {
  href: InternalUrl
}
interface ExternalLinkAttributes {
  href: string
  rel: typeof EXTERNAL_LINK_REL
  target: '_blank'
}

type LinkAttributes = InternalLinkAttributes | ExternalLinkAttributes

export const markAttributesForHref = (href: string): LinkAttributes => {
  if (isAlmanacUrl(href)) {
    return { href }
  } else {
    return { href: hasProtocol(href) ? href : `http://${href}`, rel: EXTERNAL_LINK_REL, target: '_blank' }
  }
}

const hasProtocol = (urlString: string): boolean => !!urlString.match(/^\w+:\/\//) || urlString.startsWith('mailto:')

export function makeUrl(str: string) {
  if (str.startsWith('http://') || str.startsWith('https://')) {
    return str
  }
  return `http://${str}`
}

export const linkInputRule = (schema: Schema) => {
  const linkInputRegex = new RegExp(`([\\w:/\\.-]+\\.(${COMMON_TLDS.join('|')})(\\/\\S*)?) $`)
  const createMark = ([, urlString]: Array<string>) =>
    schema.marks.link.create(markAttributesForHref(makeUrl(urlString)))

  return addMarkInputRule(linkInputRegex, createMark)
}
/**
 * EMAIL REGEXES ARE A BAD IDEA!
 * https://davidcel.is/posts/stop-validating-email-addresses-with-regex/ (random link from github copilot telling you why)
 *
 * This is set to be extremely lenient, only to help users for VERY obvious mistakes (missing . or @)
 * If you want to actually validate an email, send one.
 */
export const EMAIL_REGEX_STR = `.+@.+\\..+`
// Used to convert a string very likely containing an email to an email link
export const STRICT_EMAIL_REGEX_STR = `[\\w\\d\\._+-]+@(?:[\\w\\d-]+\\.)+(?:${COMMON_TLDS.join('|')})`

export const emailInputRule = (schema: Schema) => {
  const emailInputRegex = new RegExp(`${STRICT_EMAIL_REGEX_STR} $`)
  const createMark = (matches: Array<string>) =>
    schema.marks.link.create(markAttributesForHref(`mailto:${matches[0].trim()}`))

  return addMarkInputRule(emailInputRegex, createMark)
}

/**
 * @link being undefined will remove the `schema.mark.link`
 * @text being undefined will reuse the `selection`
 * @text being undefined and the `selection` being empty we will insert the `link` itself as the text
 *  */
export const applyLinkToSelection = (
  editorView: EditorView,
  { link, text }: { link?: string; text?: string },
): void => {
  const tr = editorView.state.tr
  const {
    selection: { from, to },
  } = editorView.state
  const selectedText = editorView.state.doc.textBetween(from, to)

  if (!link || isMarkActive(editorView.state, editorView.state.schema.marks.link)) {
    tr.removeMark(from, to, editorView.state.schema.marks.link)
  }

  if (link) {
    text ||= selectedText
    if (text === '') text = link
    const newTo = from + text.length

    tr.insertText(text)
    tr.addMark(from, newTo, editorView.state.schema.marks.link.create(markAttributesForHref(link)))
    tr.scrollIntoView()
  }
  editorView.dispatch(tr)
  editorView.focus()
}
